import { resolveBadgeLabel } from './badgeColors'

/**
 * Convierte un array de objetos a formato CSV
 */
export function convertToCSV(data: Record<string, any>[]): string {
  if (data.length === 0) return ''

  // Obtener todas las claves únicas de todos los objetos
  const headers = Array.from(
    new Set(data.flatMap(obj => Object.keys(obj)))
  )

  // Función para escapar valores CSV
  const escapeCSVValue = (value: any): string => {
    if (value === null || value === undefined) return ''
    
    const stringValue = String(value)
    
    // Si contiene coma, comillas o saltos de línea, envolver en comillas
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      return `"${stringValue.replace(/"/g, '""')}"`
    }
    
    return stringValue
  }

  // Crear el encabezado
  const headerRow = headers.map(escapeCSVValue).join(',')

  // Crear las filas de datos
  const dataRows = data.map(obj => 
    headers.map(header => escapeCSVValue(obj[header])).join(',')
  )

  return [headerRow, ...dataRows].join('\n')
}

/**
 * Descarga un string como archivo CSV
 */
export function downloadCSV(csvContent: string, filename: string): void {
  // Agregar BOM para soporte de UTF-8 en Excel
  const BOM = '\uFEFF'
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  
  if (link.download !== undefined) {
    // Crear URL del blob
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    // Liberar el URL del blob
    setTimeout(() => URL.revokeObjectURL(url), 100)
  }
}

/**
 * Formatea la fecha actual para nombres de archivo
 */
export function getFormattedDateTime(): string {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  const hours = String(now.getHours()).padStart(2, '0')
  const minutes = String(now.getMinutes()).padStart(2, '0')
  const seconds = String(now.getSeconds()).padStart(2, '0')
  
  return `${year}${month}${day}_${hours}${minutes}${seconds}`
}

/**
 * Convierte datos de estadísticas a formato para exportar usando el schema dinámico
 */
export interface ExportUser {
  id: number | string
  nombre: string
  apellido: string
  email: string
  telefono: string
  impreso: string
  fecha_impreso: string
  fecha_registro: string
  [key: string]: any // Para campos dinámicos
}

export interface SchemaField {
  label: string
  type: string
  position: number
  visible?: boolean
  [key: string]: any
}

// Columns to exclude from CSV export. Comment out a line to re-enable it.
const EXCLUDED_EXPORT_COLUMNS = new Set([
  'id',
  'ID',
  'fecha_impreso',
  'fecha_registro',
])

export function prepareStatsForExport(
  users: ExportUser[], 
  schema: Record<string, SchemaField> = {}
): Record<string, any>[] {
  // Ordenar campos del schema por position
  const schemaFields = Object.entries(schema)
    .sort(([, a], [, b]) => (a.position || 999) - (b.position || 999))
  
  return users.map(user => {
    const exportUser: Record<string, any> = {}
    
    // Iterar sobre el schema ordenado para mantener el orden de columnas
    schemaFields.forEach(([fieldName, fieldConfig]) => {
      const label = fieldConfig.label || fieldName
      
      // Buscar el valor en 3 lugares (en este orden):
      // 1. Por fieldName en raíz (ej: user['nombre'])
      // 2. Por label en raíz (ej: user['Nombre']) - para campos que el backend PHP envía así
      // 3. Por fieldName en extra
      let value = user[fieldName]
      
      if (value === undefined || value === null) {
        // Intentar buscar por label (lo que hace el backend PHP con campos extra)
        value = user[label]
      }
      
      if (value === undefined || value === null) {
        // Si no está en raíz, buscar en extra
        if (user.extra && typeof user.extra === 'object') {
          value = user.extra[fieldName]
        }
      }
      
      // Transformaciones especiales
      if (fieldName === 'impreso') {
        // Convertir boolean/number a Sí/No
        if (value === 1 || value === true || value === 'true' || value === 'Sí') {
          value = 'Sí'
        } else if (value === 0 || value === false || value === 'false' || value === 'No') {
          value = 'No'
        } else if (value === null || value === undefined || value === '') {
          value = 'No'
        }
      } else if (fieldName === 'badge_color') {
        // Convertir hex a etiqueta legible (Azul, Verde, etc.)
        const badgeLabel = resolveBadgeLabel(value)
        value = badgeLabel || value || ''
      }
      
      // Usar el label del schema como nombre de columna
      if (!EXCLUDED_EXPORT_COLUMNS.has(fieldName) && !EXCLUDED_EXPORT_COLUMNS.has(label)) {
        exportUser[label] = value !== undefined && value !== null ? value : ''
      }
    })
    
    // Agregar campos adicionales que no están en el schema
    Object.keys(user).forEach(key => {
      if (!schema[key] && key !== 'extra' && !EXCLUDED_EXPORT_COLUMNS.has(key)) {
        // Verificar que no sea un label de algún campo del schema
        const isSchemaLabel = Object.values(schema).some(field => field.label === key)
        if (!isSchemaLabel) {
          exportUser[key] = user[key]
        }
      }
    })
    
    return exportUser
  })
}
